import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { FlowBuilderError } from "./FlowBuilderError";
import { apSyncApi } from "../services/rag-service";
import { useThemeMode } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { getKeycloakToken } from "../services/keycloak";
import { tokenManager } from "../utils/tokenManager";
import { useStoredTheme } from "../utils/theme-context";
import { apFrontendUrl } from "../utils/ap-frontend-url";

// Handshake with packages/web's ExternalEmbedPage (routes/embed/external-embed.tsx):
// child sends a secret-free READY ping, parent replies with the AP token scoped to
// the flow's project. The token never travels in the iframe src/URL, only in the
// AUTH postMessage payload with an exact targetOrigin.
const EXTERNAL_EMBED_READY = "AP_EXTERNAL_EMBED_READY";
const EXTERNAL_EMBED_AUTH = "AP_EXTERNAL_EMBED_AUTH";
// Live light/dark toggle, sent any time after the handshake (the initial mode
// already travels once inside the AUTH message to avoid a flash - see
// packages/web's embedding-theme-listener.tsx for the receiver).
const EXTERNAL_EMBED_THEME_CHANGE = "AP_EXTERNAL_EMBED_THEME_CHANGE";
// On-demand pair for refreshing connectionAuthPrefill after the handshake: the
// AUTH message's copy is a one-time snapshot that can go stale (short Keycloak
// TTL) by the time a connection dialog actually opens.
const CONNECTION_AUTH_PREFILL_REQUEST = "AP_CONNECTION_AUTH_PREFILL_REQUEST";
const CONNECTION_AUTH_PREFILL_RESPONSE = "AP_CONNECTION_AUTH_PREFILL_RESPONSE";
// One-way push (no request half) - this side knows when a refresh is due, so
// it doesn't wait to be asked. Replaces only the context token.
const CONTEXT_TOKEN_REFRESH = "AP_CONTEXT_TOKEN_REFRESH";
// Sent by the builder when its token is about to expire before a request
// (e.g. after laptop sleep, when the interval below missed its tick). Answered
// with the same CONTEXT_TOKEN_REFRESH push.
const CONTEXT_TOKEN_REFRESH_REQUEST = "AP_CONTEXT_TOKEN_REFRESH_REQUEST";
// The context token has a hard 5-minute TTL (see ap_context_token.py) and
// nothing re-mints it after the handshake - left alone, a long session falls
// back to coarser org-wide scoping. Refreshing well inside the TTL avoids that.
const CONTEXT_TOKEN_REFRESH_INTERVAL_MS = 3 * 60 * 1000;
const BUILDER_LOAD_TIMEOUT_MS = 30000;

// apFrontendUrl is AP's own frontend (packages/web), same origin as its API -
// the builder's calls are same-origin once loaded, and it tracks upstream AP
// on every rebase with no re-vendoring.

// Lets a SECRET_TEXT connection dialog skip a manual token paste by
// suggesting the caller's own identity - the iframe can't do the legacy
// same-origin cookie read, so this reuses getKeycloakToken() instead. Returns
// undefined if not authenticated; AP's dialog falls back to manual entry.
//
// Refreshes first because getKeycloakToken() only reads the cached token -
// this app's refresh is just-in-time via the request interceptor, so without
// this call the token could already be expired.
async function getConnectionAuthPrefill(currentUser, currentOrganization) {
  try {
    await tokenManager.refreshIfExpired();
  } catch (error) {
    console.error(
      "ApBuilderIframe: token refresh failed before building auth prefill",
      error,
    );
  }
  const token = getKeycloakToken();
  if (!token || !currentUser?.email) {
    return undefined;
  }
  // currentUser is raw Keycloak user info - firstName/lastName/username, no
  // single "name" field.
  const fullName = `${currentUser.firstName || ""} ${
    currentUser.lastName || ""
  }`.trim();
  const name = fullName || currentUser.username || currentUser.email;
  return {
    // Matches the legacy BunyanCustomAuth's own connection-name suggestion
    // exactly ("{name}<{email}>").
    connectionDisplayName: `${name}<${currentUser.email}>`,
    name,
    secretValue: currentOrganization?.id
      ? `${token},${currentOrganization.id}`
      : token,
    organizationSlug: currentOrganization?.slug,
  };
}

// Re-mints the context token and pushes it into the builder. Also a live
// revocation check: a member removed mid-session stops getting a usable token.
async function pushFreshContextToken(iframeRef, realProjectId) {
  const apSession = await apSyncApi.getApSession(realProjectId);
  iframeRef.current?.contentWindow?.postMessage(
    {
      type: CONTEXT_TOKEN_REFRESH,
      data: { contextToken: apSession.contextToken },
    },
    apFrontendUrl,
  );
}

export function ApBuilderIframe({ projectId, flowId, tenantId, realProjectId, onBack }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const storedTheme = useStoredTheme();
  const accentColor = storedTheme?.button?.primary?.backgroundColor?.[mode];
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const latestRef = useRef({});
  latestRef.current = { mode, accentColor, currentUser, currentOrganization };
  const currentUserId = currentUser?.id;

  useEffect(() => {
    let active = true;
    let authenticating = false;
    const controller = new AbortController();
    const handleMessage = async (event) => {
      // Only trust messages from the exact AP frontend origin, from this very
      // iframe's own content window - never a wildcard, never a sibling frame.
      if (
        event.origin !== apFrontendUrl ||
        event.source !== iframeRef.current?.contentWindow
      ) {
        return;
      }
      if (event.data?.type === CONNECTION_AUTH_PREFILL_REQUEST) {
        // Secret-bearing reply: exact targetOrigin, never "*" (matches the
        // AUTH message below, unlike the secret-free READY/REQUEST pings).
        const connectionAuthPrefill = await getConnectionAuthPrefill(
          latestRef.current.currentUser,
          latestRef.current.currentOrganization,
        );
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: CONNECTION_AUTH_PREFILL_RESPONSE,
            data: { connectionAuthPrefill },
          },
          apFrontendUrl,
        );
        return;
      }
      if (event.data?.type === CONTEXT_TOKEN_REFRESH_REQUEST) {
        try {
          await pushFreshContextToken(iframeRef, realProjectId);
        } catch (error) {
          console.error("ApBuilderIframe: on-demand context token refresh failed", error);
        }
        return;
      }
      if (event.data?.type !== EXTERNAL_EMBED_READY || ready || error || authenticating) {
        return;
      }
      authenticating = true;
      try {
        const apSession = await apSyncApi.getApSession(realProjectId, {
          signal: controller.signal,
          timeout: BUILDER_LOAD_TIMEOUT_MS,
        });
        if (!apSession.token || !apSession.contextToken) {
          throw new Error("Flow builder session is unavailable.");
        }
        const connectionAuthPrefill = await getConnectionAuthPrefill(
          latestRef.current.currentUser,
          latestRef.current.currentOrganization,
        );
        if (!active) return;
        const { mode: currentMode, accentColor: currentAccentColor } =
          latestRef.current;
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: EXTERNAL_EMBED_AUTH,
            data: {
              token: apSession.token,
              // The flow's resolved AP project home (shared-project aware), NOT the
              // caller's own personal projectId from getApSession - matches what the
              // <Omniflow> backup path pushes via apTokenManager.setProjectId.
              projectId,
              flowId,
              // Real org id (org-scoped project) or user id (personal project) - lets
              // the embedded builder tag resources it creates directly (e.g. app
              // connections) with a real, grantable externalId instead of AP's own
              // random fallback, which the Bunyan authorization hook always rejects.
              tenantId,
              // Signed, short-lived token (see ap_context_token.py) - lets AP's
              // authorization hook scope by project and role. Null if realProjectId
              // wasn't provided or minting failed; must never mean "full access".
              contextToken: apSession.contextToken,
              // Host shell's CURRENT light/dark mode, so the builder opens in
              // the right mode from its very first paint instead of always
              // dark-then-flip (see THEME_CHANGE below for later toggles).
              theme: currentMode,
              // This org's primary button color for the current mode, so the
              // embedded builder matches the host shell instead of AP's own
              // static color. Undefined for an org with no custom theme.
              accentColor: currentAccentColor,
              // Optional SECRET_TEXT identity suggestion (see
              // getConnectionAuthPrefill above). Sent on every handshake since
              // this component doesn't know yet which piece dialog, if any,
              // will open.
              connectionAuthPrefill,
            },
          },
          apFrontendUrl,
        );
        setReady(true);
      } catch (error) {
        if (active) setError(error);
      } finally {
        authenticating = false;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      active = false;
      controller.abort();
      window.removeEventListener("message", handleMessage);
    };
  }, [
    projectId,
    flowId,
    tenantId,
    realProjectId,
    currentUserId,
    ready,
    error,
  ]);

  useEffect(() => {
    if (ready || error) return;
    const timeoutId = setTimeout(
      () => setError(new Error("Flow builder loading timed out.")),
      BUILDER_LOAD_TIMEOUT_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [ready, error]);

  // Live theme sync after the handshake - the initial mode already travelled
  // once inside the AUTH message; this pushes any later change (e.g. the host
  // shell's dark-mode toggle).
  useEffect(() => {
    if (!ready) {
      return;
    }
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: EXTERNAL_EMBED_THEME_CHANGE,
        data: { theme: mode, accentColor },
      },
      apFrontendUrl,
    );
  }, [mode, accentColor, ready]);

  // Proactive context-token re-mint. A failed refresh is logged and skipped;
  // the next tick (or the builder's own on-demand request) retries rather
  // than tearing down a working iframe.
  useEffect(() => {
    if (!ready || error) {
      return;
    }
    const intervalId = setInterval(async () => {
      try {
        await pushFreshContextToken(iframeRef, realProjectId);
      } catch (error) {
        console.error(
          "ApBuilderIframe: context token refresh failed, will retry next interval",
          error,
        );
      }
    }, CONTEXT_TOKEN_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [ready, error, realProjectId]);

  if (error) {
    return (
      <FlowBuilderError
        error={error}
        onBack={onBack}
        onRetry={() => {
          setReady(false);
          setError(null);
        }}
      />
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {!ready && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: theme.palette.background.default,
            zIndex: 1,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t("common.loading")}
          </Typography>
        </Box>
      )}
      <iframe
        ref={iframeRef}
        title="Flow Builder"
        src={`${apFrontendUrl}/embed/external`}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </Box>
  );
}
